import * as React from 'react';
import { FilterType, ITrackedBolFileModel, ITrackedBolFileType, JoinType } from '../Api/Kopp/Api';
import equals from 'lodash/isEqual';
import { ValidationRule } from 'antd/lib/form';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import { Moment } from 'moment';

export default class ValidationUtility {
  public static getRequired(message: React.ReactNode): ValidationRule[] {
    return [{ required: true, message: message }];
  }

  public static getPattern(pattern: RegExp, message: React.ReactNode): ValidationRule[] {
    return [{ pattern: pattern, message: message }];
  }

  public static getType(type: string, message: React.ReactNode): ValidationRule[] {
    return [{ type: type, message: message }];
  }

  public static getStrongPassword() {
    return [
      {
        pattern: /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,}/,
        message: this.renderStrongPasswordErrorMessages(),
      },
    ];
  }

  public static renderStrongPasswordErrorMessages() {
    return (
      <ul style={{ textAlign: 'left' }}>
        <li>Password must contain at least one number</li>
        <li>One lowercase and one uppercase letter</li>
        <li>At least six characters that are letters, numbers or the underscore</li>
      </ul>
    );
  }

  public static getName(): Array<ValidationRule> {
    return [
      { required: true, message: 'Please enter both your first and last names' },
      { max: 30, message: 'Limit of 30 characters' },
    ];
  }

  public static getMobilenumber(): ValidationRule[] {
    return [
      { required: true, message: 'Please enter a mobile number' },
      { pattern: /[0-9]{3}-[0-9]{3}-[0-9]{4}/, message: 'Please enter valid Mobile Number' },
    ];
  }

  public static getAddress(): ValidationRule[] {
    return [
      { required: true, message: 'Please enter an address' },
      { max: 255, message: 'You have character limit' },
    ];
  }

  public static getCompareField(
    form: WrappedFormUtils,
    field: string,
    message: React.ReactNode
  ): ValidationRule[] {
    const validator = (rule, value, callback) => {
      if (value && value !== form.getFieldValue(field)) {
        callback(message);
      } else {
        callback();
      }
    };

    return [
      {
        validator: validator,
      },
    ];
  }

  public static validateOtherFields(
    form: WrappedFormUtils,
    field: string | string[]
  ): ValidationRule[] {
    const validator = (rule, value, callback) => {
      if (value) {
        // if fields is a single string, turn it into an array
        const fields = (field as Array<string>).map != undefined
          ? (field as Array<string>)
          : ([field] as Array<string>);
        form.validateFields(fields, { force: true });
      }
      callback();
    };

    return [
      {
        validator: validator,
      },
    ];
  }

  public static getCheckBoxChecked(message: string): ValidationRule[] {
    const validator = (rule, value, callback) => {
      if (!value) {
        callback(message);
      } else {
        callback();
      }
    };

    return [
      {
        validator: validator,
      },
    ];
  }

  public static getIsANumber(message?: string): ValidationRule[] {
    const validator = (rule, value, callback) => {
      if (Number.isInteger(value)) {
        callback(message || 'This is not a valid number');
      }
      callback();
    };

    return [
      {
        validator: validator,
      },
    ];
  }

  public static getNotInThePassDate(message?: string): ValidationRule[] {
    message = message ?? "You cannot select a date in the past for Jamaica";
    const validator = (rule, value, callback) => {
      if (!value) {
        callback();
        return;
      }
      let valueAsMoment = value as Moment;

      if (valueAsMoment) {
        let valueInJamaica = valueAsMoment.utcOffset(-5, false);

        if (valueInJamaica.isBefore(undefined, "day")) {
          callback(message);
        } else {
          callback();
        }

      }


    };

    return [
      {
        validator: validator,
      },
    ];
  }

  public static getCreditCardNumber(
    form: WrappedFormUtils,
    cardTypeField: string
  ): ValidationRule[] {
    const validator = ValidationUtility.CreditCardNumberValiatorMethodGenerator(
      form,
      cardTypeField
    );

    return [
      {
        validator: validator,
      },
    ];
  }

  /**
   * This is a message that reutnrs a valiator method (used in ant.design)
   * to validate the card number against the card type selected.
   *
   * @static
   * @memberof ValidationUtility
   * @returns Validator
   */
  public static CreditCardNumberValiatorMethodGenerator = (
    form: WrappedFormUtils,
    cardTypeField: string
  ) => (rule, value, callback) => {
    let cardNumber: string = '';

    if (!value) {
      callback('Please enter a valid card number.');
      return;
    }

    let cardTypeValue = form.getFieldValue(cardTypeField);

    // If you don't have the card type value, then you can't validate yet
    if (!cardTypeValue || cardTypeValue == 'undefined') {
      callback();
      return;
    }

    // Done to accomodate for spacing because of mask applied
    let cardNumberInput = value;
    let splitCardNumber = cardNumberInput.split(' ');
    cardNumber = splitCardNumber.join('');

    let cardTypesDictionary = ValidationUtility.getCardTypeDictionary();

    // Check the patterns
    for (const cardType in cardTypesDictionary) {
      if (cardTypesDictionary.hasOwnProperty(cardType)) {
        const value = cardTypesDictionary[cardType];
        value.is = value.pattern.test(cardNumber);
      }
    }

    let cardType = cardTypesDictionary[cardTypeValue];

    if (!cardType) {
      throw Error('Trying to Validate a card type that is not defined: ' + cardTypeValue);
    }

    if (cardType.is) {
      callback();
    } else {
      callback(cardType.message);
    }
  };

  static getCardNumberTransform() {
    const transform = (value: string | undefined) => {
      return value?.replace(/\s*/g, '');;
    };

    return [{ transform }];
  }

  static getCardTypeDictionary() {
    let cardTypesDictionary: { [key: string]: { pattern: RegExp; is: Boolean; message: string } };

    cardTypesDictionary = {};
    cardTypesDictionary['VISA'] = {
      pattern: /^(?:4[0-9]{12}(?:[0-9]{3})?)$/,
      is: false,
      message: 'Please provide a valid Visa number!',
    };
    cardTypesDictionary['MASTERCARD'] = {
      pattern: /^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$/,
      is: false,
      message: 'Please provide a valid MasterCard number!',
    };
    cardTypesDictionary['KEYCARD'] = {
      pattern: /^(?:7[0-9]{15})$/,
      is: false,
      message: 'Please provide a valid Keycard number!',
    };
    cardTypesDictionary['a'] = {
      pattern: /^(?:3[47][0-9]{13})$/,
      is: false,
      message: 'Please provide a valid American Express number!',
    };
    cardTypesDictionary['d'] = {
      pattern: /^(?:6(?:011|5[0-9][0-9])[0-9]{12})$/,
      is: false,
      message: 'Please provide a valid Discovery number!',
    };
    return cardTypesDictionary;
  }

  public static validateExpiryDate = () => (rule, value, callback) => {
    // let expiryDate: number;

    if (!value) {
      callback('Please enter an Expiry Date!');
    }

    const expiryDateValue = value;

    if (!expiryDateValue || expiryDateValue === 'undefined') {
      callback();
      return;
    }

    // getting split values
    const currentDate = new Date();
    const currentTwoDigitYear = currentDate.getFullYear().toString().substr(-2);;
    const currentMonth = currentDate.getMonth() + 1;

    const expiryMonth = expiryDateValue.split('/', 1)[0];
    const splitExpiry = expiryDateValue.split('/');
    const expiryYear = splitExpiry[1];

    //checking if both month & date are invalid
    if (expiryYear < currentTwoDigitYear && (expiryYear <= currentTwoDigitYear && expiryMonth < currentMonth) || expiryYear < currentTwoDigitYear && expiryMonth > 12) {
      callback('Please enter valid Expiry Date!');
    }
    // check if year is valid
    else if (expiryYear < currentTwoDigitYear) {
      callback('Please enter valid Expiry Date!');
    }
    // checking if month less than current month in current year or less(which would make year also invalid)
    else if (expiryYear <= currentTwoDigitYear && expiryMonth < currentMonth) {
      callback('Please enter valid Expiry Date!');
    }

    //checking if expiry month is greater than 12 while year is valid
    else if (expiryMonth > 12 && expiryYear >= currentTwoDigitYear) {
      callback('Please enter valid Expiry Date!');
    }

    callback();
  };

  // static getExpiryDatePattern() {
  //   let expiryDatePattern: { [key: string]: { pattern: RegExp; is: Boolean; message: string } };

  //   expiryDatePattern = {};
  //   expiryDatePattern[''] = {
  //     pattern: /^(0[1-9]|1[0-2])\/?([0-9]{4})$/,
  //     is: false,
  //     message: 'Please provide a valid Expiry Date!',
  //   };
  // }

  public static getIsValidExpiryDate(): ValidationRule[] {
    const validator = ValidationUtility.validateExpiryDate();

    return [
      {
        validator,
      },
    ];
  }

  public static getChargedAmount() {
    return [
      {
        pattern: /^[0-9]+\.[0-9]+$/,
        message: 'Only numbers and one decimal sign is accepted',
      },
    ];
  }

  public static getBolFileIdRequired(
    fileTypes?: ITrackedBolFileType[],
    message?: React.ReactNode
  ): ValidationRule[] {

    let finalMessage = message ?? "Identification is required"
    const validator = async (rule, value: ITrackedBolFileModel[], callback) => {

      // Don't show if value is empty
      if (!value || !value.length) callback();

      let uploadedFilesTypes: ITrackedBolFileType[] = [];

      //match all uploaded file with a fileType
      for (var i = 0; i < value.length; ++i) {
        let match = fileTypes?.find(x => x.id == value[i].bolFileTypeId);
        uploadedFilesTypes.push(match!);
      }

      //return true if fileType of any identification exists      
      const hasIdFile = uploadedFilesTypes.some(x => {
        if (x.name == "Drivers Licence" || x.name == "National ID" || x.name == "Passport") {
          return true;
        }
        else {
          return false;
        }
      });

      if (!hasIdFile) callback(finalMessage);
      else callback();
    };

    return [
      {
        validator: validator,
      },
    ];
  }
}
